import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '../hooks/useScrollAnimation';
import { useCountUp } from '../hooks/useCountUp';
function StatCard({
  value,
  label,
  context,
  accent = false
}: {
  value: string;
  label: string;
  context: string;
  accent?: boolean;
}) {
  return <motion.div variants={fadeInUp} className="p-8 bg-dark-card border border-dark-border rounded-xl" data-id="element-205">
      <div className={`text-4xl md:text-5xl font-bold mb-3 ${accent ? 'text-gold' : 'text-white'}`} data-id="element-206">
        {value}
      </div>
      <div className="text-white font-semibold text-base mb-1" data-id="element-207">{label}</div>
      <div className="text-slate-500 text-sm leading-relaxed" data-id="element-208">{context}</div>
    </motion.div>;
}
function AllocationRow({
  label,
  value,
  pct,
  accent = false
}: {
  label: string;
  value: string;
  pct: number;
  accent?: boolean;
}) {
  return <div className="mb-5 last:mb-0" data-id="element-209">
      <div className="flex justify-between items-baseline mb-2" data-id="element-210">
        <span className="text-slate-300 text-sm" data-id="element-211">{label}</span>
        <span className="text-white font-semibold text-sm font-mono" data-id="element-212">
          {value}
        </span>
      </div>
      <div className="h-1 w-full bg-dark-elevated rounded-full overflow-hidden" data-id="element-213">
        <motion.div className={`h-full rounded-full ${accent ? 'bg-gold' : 'bg-white/30'}`} initial={{
        width: 0
      }} whileInView={{
        width: `${pct}%`
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} viewport={{
        once: true
      }} data-id="element-214" />
      </div>
    </div>;
}
export function EnterpriseScopeSection() {
  return <section id="enterprise-scope" className="py-20 md:py-32 bg-dark-bg relative border-t border-dark-border/30" data-id="element-215">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-216">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} data-id="element-217">
          <motion.div variants={fadeInUp} className="mb-16" data-id="element-218">
            <span className="font-sans text-gold/80 tracking-widest uppercase text-xs font-semibold mb-2 block" data-id="element-219">
              Enterprise Scope
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white" data-id="element-220">
              Operating at Global Scale
            </h2>
          </motion.div>

          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12" data-id="element-221">
            <StatCard value="$1.2B" label="Technology Budget" context="Annual portfolio managed at Truist Financial" accent data-id="element-222" />
            <StatCard value="1,700+" label="Engineers Led" context="Global organization across 40+ countries" data-id="element-223" />
            <StatCard value="$140M" label="Annual Savings" context="Delivered through cloud optimization" accent data-id="element-224" />
            <StatCard value="50,000+" label="VMs Migrated" context="Hybrid cloud transition per year" data-id="element-225" />
          </div>

          {/* Budget Allocation */}
          <motion.div variants={fadeInUp} className="bg-dark-card border border-dark-border rounded-xl p-8" data-id="element-226">
            <h3 className="text-white font-bold text-lg mb-2" data-id="element-227">
              Budget Allocation Strategy
            </h3>
            <p className="text-slate-500 text-sm mb-8" data-id="element-228">
              How a $1.2B technology portfolio is structured for resilience and
              growth.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-0" data-id="element-229">
              <div data-id="element-230">
                <AllocationRow label="Infrastructure & Operations" value="$480M" pct={40} data-id="element-231" />
                <AllocationRow label="Cloud Services & SaaS" value="$360M" pct={30} data-id="element-232" />
              </div>
              <div data-id="element-233">
                <AllocationRow label="Cybersecurity & Compliance" value="$216M" pct={18} accent data-id="element-234" />
                <AllocationRow label="Innovation & R&D" value="$144M" pct={12} data-id="element-235" />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}